import React, {
  useState, useEffect, useLayoutEffect, useCallback, useRef,
} from 'react';
import {
  Typography,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Modal,
  Fade,
  Box,
  IconButton,
  Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  AccountBalanceWalletOutlined as BalanceIcon,
  InboxOutlined as EmptyIcon,
  RefreshRounded as RefreshIcon,
  ReceiptLongOutlined as LedgerIcon,
  TrendingDownRounded as RepayIcon,
  TrendingUpRounded as BorrowIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../redux/actions';
import { BORROW_PROJECT, REPAY_PROJECT } from '../../constants/loanProjects';
import { formatCurrency } from '../../utils/format';
import ExpensesDetail from '../../../Finance/ExpensesDetail';

// แปลง flat expense rows (ของฝั่งเดียว) เป็นแถวตาราง — 1 expense item = 1 แถวเสมอ ไม่รวม
// รายการเบิกหลายรายการของ Payment เดียวกันเข้าด้วยกัน (ต่างจากเดิมที่เคย group ตาม
// payment_id) ถ้า Payment ใบเดียวมี 2 รายการเบิก จะเห็น 2 แถวที่มีรหัส Payment ซ้ำกัน
// คนละยอด/คนละหมายเหตุ — field ระดับ Payment เอง (payment_number/date/createBy/status)
// จะซ้ำกันทุกแถวของ Payment เดียวกันโดยธรรมชาติ (มาจาก field เดียวกันในเอกสารต้นทาง)
function toEntries(rows, direction) {
  return rows.map((row) => ({
    key: row.id,
    expensesId: row.expenses_id,
    payment_id: row.payment_id,
    payment_number: row.payment_number,
    date: row.date,
    direction,
    createBy: row.create_by || '',
    status: row.status,
    name: row.name,
    amount: Number(row.amount || 0),
  }));
}

// ตารางรายการยืม-คืนเงินแบบเดียวกับสมุดจดกระดาษของทีมบัญชี (วันที่โอน/รหัส/ยอดยืม/
// ยอดคืน/คงเหลือ/สถานะ/ลงชื่อ/หมายเหตุ) ดึงรายการยืม (ฝั่งยืม) + คืน (ฝั่งคืน) จาก
// Payment จริงมารวมกันเรียงตามวันที่ แล้วคำนวณ "คงเหลือ" สะสมสด
//
// "กำหนดยอดคงเหลือ" ไม่ได้ตัดรายการเก่าทิ้ง — เพิ่มเป็นแถวใหม่คั่นกลางแทน (เหมือนสมุด
// จดจริงที่เขียนบรรทัด "ยอดยกมา" คั่นแล้วเขียนต่อ ไม่ได้ฉีกหน้าเก่าทิ้ง): รายการก่อน
// dateFrom (opening_balance_date) ยังคำนวณคงเหลือแบบเดิมทุกประการ (สะสมจาก 0) ส่วน
// รายการหลัง dateFrom เริ่มคำนวณใหม่จาก openingBalance แทน — ถ้ายังไม่เคยกำหนดยอด
// คงเหลือเลย (dateFrom ว่าง) ทุกรายการยังคำนวณจาก 0 เหมือนเดิมทั้งตาราง
//
// column สถานะ/ลงชื่อ มาจาก Payment.status / Payment.create_by ตรงๆ — คอลัมน์หมายเหตุ
// ไม่มี chip ยืนยัน OCR อีกต่อไป (หน้านี้ปิดการยืนยัน OCR ถาวรแล้ว ดู ocrEnabled ใน
// LoanPairSummaryCard.js)
export function LoanLedgerTable({
  customerId,
  customerIdRepay,
  label,
  dateFrom,
  openingBalance = 0,
}) {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const [beforeRows, setBeforeRows] = useState([]);
  const [afterRows, setAfterRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ดับเบิลคลิกแถว → เปิด modal "รายละเอียดรายการเบิกจ่าย" เหมือนกับ ExpensesTableApplove.js
  // (component เดียวกัน <ExpensesDetail propId={...}> ไม่ใช่ก็อปโค้ดมาเขียนใหม่) — propId
  // ต้องเป็น _id ของ Expenses ใน collection จริง คือ row.expensesId (= expenses_list.expenses_id
  // ฝั่ง backend) ห้ามใช้ row.key (= expenses_list._id, แค่ subdocument id ใช้เป็น React key
  // เท่านั้น) เด็ดขาด เคยพลาดใช้ row.key มาก่อนแล้ว expensesGet(id) ยิง 404 เพราะ id นั้นไม่มี
  // อยู่จริงใน Expenses collection
  const [detailExpenseId, setDetailExpenseId] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const openDetail = (id) => {
    setDetailExpenseId(id);
    setDetailModalOpen(true);
  };
  const closeDetail = () => setDetailModalOpen(false);

  const load = useCallback(async () => {
    if (!customerId || !customerIdRepay) return;
    setLoading(true);
    setError('');
    try {
      const [borrow, repay] = await Promise.all([
        dispatch(
          actions.listLoanProjectExpenses({
            customerId,
            projectName: BORROW_PROJECT.name,
            projectNumber: BORROW_PROJECT.number,
          }),
        ),
        dispatch(
          actions.listLoanProjectExpenses({
            customerId: customerIdRepay,
            projectName: REPAY_PROJECT.name,
            projectNumber: REPAY_PROJECT.number,
          }),
        ),
      ]);

      const merged = [
        ...toEntries(borrow.rows, 'BORROW'),
        ...toEntries(repay.rows, 'REPAY'),
      ].sort((a, b) => new Date(a.date) - new Date(b.date));

      const beforeCutoff = dateFrom
        ? merged.filter((entry) => new Date(entry.date) < new Date(dateFrom))
        : merged;
      const afterCutoff = dateFrom
        ? merged.filter((entry) => new Date(entry.date) >= new Date(dateFrom))
        : [];

      let runningBefore = 0;
      const beforeWithBalance = beforeCutoff.map((entry) => {
        runningBefore += entry.direction === 'BORROW' ? entry.amount : -entry.amount;
        return { ...entry, balance: runningBefore };
      });

      let runningAfter = openingBalance;
      const afterWithBalance = afterCutoff.map((entry) => {
        runningAfter += entry.direction === 'BORROW' ? entry.amount : -entry.amount;
        return { ...entry, balance: runningAfter };
      });

      setBeforeRows(beforeWithBalance);
      setAfterRows(afterWithBalance);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [customerId, customerIdRepay, dispatch, dateFrom, openingBalance]);

  useEffect(() => {
    load();
  }, [load]);

  // เริ่มต้นให้ scroll ไปอยู่ล่างสุด (รายการล่าสุด) แทนบนสุด (รายการเก่าสุด)
  //
  // ต้องมี loading อยู่ใน dependency array ด้วย ไม่ใช่แค่ [beforeRows, afterRows] — เพราะ
  // โปรเจกต์นี้ยังใช้ React 17 (ไม่มี automatic batching นอก event handler) setBeforeRows/
  // setAfterRows/setLoading(false) ใน load() เป็น 3 setState แยกกันคนละ render จริงๆ:
  // (1) setBeforeRows → render, loading ยังเป็น true อยู่ → TableContainer ยังไม่ถูก mount
  // (ยังโชว์ CircularProgress) → scrollRef.current เป็น null เฉยๆ (2) setAfterRows →
  // เหมือนเดิม ยัง null (3) setLoading(false) → TableContainer เพิ่ง mount ครั้งแรกจริง
  // ตอนนี้ scrollRef.current มีค่าแล้ว แต่ถ้า deps มีแค่ [beforeRows, afterRows] จะไม่
  // เปลี่ยนในรอบนี้ (ค่าเดิมจากรอบ 1-2) effect เลย "ไม่รัน" ทั้งที่เป็นจังหวะเดียวที่ ref
  // ใช้งานได้จริง — ผลคือค้างที่บนสุดตลอดเพราะไม่เคย scroll สำเร็จเลยสักครั้ง
  useLayoutEffect(() => {
    if (!loading && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [beforeRows, afterRows, loading]);

  const allRows = [...beforeRows, ...afterRows];
  const borrowTotal = allRows.reduce(
    (sum, row) => sum + (row.direction === 'BORROW' ? row.amount : 0),
    0,
  );
  const repayTotal = allRows.reduce(
    (sum, row) => sum + (row.direction === 'REPAY' ? row.amount : 0),
    0,
  );
  const currentBalance = afterRows.length > 0
    ? afterRows[afterRows.length - 1].balance
    : dateFrom
      ? Number(openingBalance || 0)
      : beforeRows.length > 0
      ? beforeRows[beforeRows.length - 1].balance
      : Number(openingBalance || 0);

  const dateKey = (value) => {
    const date = new Date(value);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  const renderRow = (row, index, groupRows) => {
    const noteText = row.name || '-';
    const startsNewDate = index > 0
      && dateKey(row.date) !== dateKey(groupRows[index - 1].date);
    return (
      <TableRow
        key={`${row.direction}-${row.key}`}
        onDoubleClick={() => openDetail(row.expensesId)}
        sx={{
          cursor: 'pointer',
          '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
          '&:hover': { bgcolor: '#eff6ff !important' },
          '& td': {
            borderColor: '#e8edf3',
            borderTop: startsNewDate ? '2px solid #94a3b8' : undefined,
            py: 1.25,
          },
        }}
      >
        <TableCell sx={{ whiteSpace: 'nowrap', color: '#475569' }}>{new Date(row.date).toLocaleDateString('th-TH')}</TableCell>
        <TableCell sx={{ fontWeight: 650, color: '#334155', whiteSpace: 'nowrap' }}>{row.payment_number}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 750, color: row.direction === 'BORROW' ? '#15803d' : '#94a3b8', bgcolor: row.direction === 'BORROW' ? 'rgba(34,197,94,.055)' : 'transparent' }}>
          {row.direction === 'BORROW' ? formatCurrency(row.amount) : '-'}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 750, color: row.direction === 'REPAY' ? '#dc2626' : '#94a3b8', bgcolor: row.direction === 'REPAY' ? 'rgba(239,68,68,.05)' : 'transparent' }}>
          {row.direction === 'REPAY' ? formatCurrency(row.amount) : '-'}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>{formatCurrency(row.balance)}</TableCell>
        <TableCell>
          <Chip
            size="small"
            color={row.status === 'SUCCESS' ? 'success' : 'warning'}
            variant="outlined"
            label={row.status === 'SUCCESS' ? 'อนุมัติ' : 'รออนุมัติ'}
            sx={{ fontWeight: 650 }}
          />
        </TableCell>
        <TableCell>{row.createBy || '-'}</TableCell>
        <TableCell sx={{ maxWidth: 200 }}>
          <Tooltip title={noteText}>
            <Typography variant="body2" noWrap>{noteText || '-'}</Typography>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Paper variant="outlined" sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', borderRadius: 3, borderColor: '#e2e8f0', overflow: 'hidden' }}>
      <Box sx={{ p: { xs: 1.5, md: 1.75 }, borderBottom: '1px solid', borderColor: '#e2e8f0', bgcolor: '#fff', flexShrink: 0 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: '#eef2ff', color: '#4f46e5' }}>
              <LedgerIcon fontSize="small" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 750 }}>{label || 'รายการยืม-คืนเงิน'}</Typography>
              <Typography variant="caption" color="text.secondary">ดับเบิลคลิกเพื่อดูรายละเอียด</Typography>
            </Box>
          </Stack>
          <Tooltip title="โหลดข้อมูลล่าสุด">
            <span>
              <IconButton onClick={load} disabled={loading} aria-label="โหลดข้อมูลล่าสุด" sx={{ border: '1px solid', borderColor: '#e2e8f0' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {!loading && !error && allRows.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 0.75, mt: 1.25 }}>
            <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <Stack direction="row" spacing={0.75} alignItems="center"><BorrowIcon sx={{ fontSize: 18, color: '#15803d' }} /><Typography variant="caption" sx={{ color: '#166534', fontWeight: 700 }}>ยอดยืมรวม</Typography></Stack>
              <Typography sx={{ color: '#15803d', fontWeight: 800 }}>{formatCurrency(borrowTotal)} บาท</Typography>
            </Box>
            <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
              <Stack direction="row" spacing={0.75} alignItems="center"><RepayIcon sx={{ fontSize: 18, color: '#dc2626' }} /><Typography variant="caption" sx={{ color: '#991b1b', fontWeight: 700 }}>ยอดคืนรวม</Typography></Stack>
              <Typography sx={{ color: '#dc2626', fontWeight: 800 }}>{formatCurrency(repayTotal)} บาท</Typography>
            </Box>
            <Box sx={{ px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Stack direction="row" spacing={0.75} alignItems="center"><BalanceIcon sx={{ fontSize: 18, color: '#475569' }} /><Typography variant="caption" sx={{ color: '#475569', fontWeight: 700 }}>ยอดคงเหลือล่าสุด</Typography></Stack>
              <Typography sx={{ color: '#0f172a', fontWeight: 800 }}>{formatCurrency(currentBalance)} บาท</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center' }}>
          <Stack spacing={1.25} alignItems="center"><CircularProgress size={28} /><Typography variant="body2" color="text.secondary">กำลังโหลดรายการ...</Typography></Stack>
        </Box>
      ) : beforeRows.length === 0 && afterRows.length === 0 && !dateFrom ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'grid', placeItems: 'center', textAlign: 'center', bgcolor: '#f8fafc' }}>
          <Box>
          <EmptyIcon sx={{ fontSize: 44, color: '#94a3b8', mb: 1 }} />
          <Typography sx={{ fontWeight: 700 }}>ยังไม่มีรายการยืม-คืน</Typography>
          <Typography variant="body2" color="text.secondary">เมื่อมีรายการ Payment ข้อมูลจะแสดงที่นี่อัตโนมัติ</Typography>
          </Box>
        </Box>
      ) : (
        <TableContainer ref={scrollRef} sx={{ flex: 1, minHeight: 0, maxHeight: 'none', overflowX: 'auto', overflowY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f1f5f9', color: '#475569', fontWeight: 750, borderColor: '#dbe3ec', py: 1.4, whiteSpace: 'nowrap' } }}>
                <TableCell>วันที่โอน</TableCell>
                <TableCell>รหัส Payment</TableCell>
                <TableCell align="right" sx={{ color: '#15803d !important', bgcolor: '#ecfdf5 !important' }}>ยอดยืม (บาท)</TableCell>
                <TableCell align="right" sx={{ color: '#dc2626 !important', bgcolor: '#fef2f2 !important' }}>ยอดคืน (บาท)</TableCell>
                <TableCell align="right">คงเหลือ (บาท)</TableCell>
                <TableCell>สถานะ</TableCell>
                <TableCell>ผู้ทำรายการ</TableCell>
                <TableCell>หมายเหตุ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {beforeRows.map(renderRow)}
              {dateFrom && (
                <TableRow sx={{ bgcolor: '#fff7ed', '& td': { borderTop: '1px solid #fed7aa', borderBottom: '1px solid #fed7aa', py: 1.25 } }}>
                  <TableCell>{new Date(dateFrom).toLocaleDateString('th-TH')}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: '#9a3412' }}>{formatCurrency(openingBalance)}</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell><Chip size="small" label="ยอดยกมา" sx={{ bgcolor: '#ffedd5', color: '#9a3412', fontWeight: 700 }} /></TableCell>
                </TableRow>
              )}
              {afterRows.map(renderRow)}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Modal open={detailModalOpen} onClose={closeDetail} closeAfterTransition>
        <Fade in={detailModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '98%', sm: '95%', md: '90%', lg: '82%' },
              maxHeight: '95vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'primary.main',
                color: 'white',
                flexShrink: 0,
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                รายละเอียดรายการเบิกจ่าย
              </Typography>
              <IconButton size="small" onClick={closeDetail} sx={{ color: 'white' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1, p: { xs: 1, sm: 2 } }}>
              {detailExpenseId && (
                <ExpensesDetail propId={detailExpenseId} onClose={closeDetail} />
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Paper>
  );
}

export default LoanLedgerTable;
